import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { format, parseISO, isAfter, isBefore, isEqual } from 'date-fns';
import { Loader2, History, Calendar, Pencil, Trash2, X, Check, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { BrandAutocomplete } from './BrandAutocomplete';

interface NutritionHistoryProps {
  petId: string;
  onHistoryChange?: () => void;
}

interface HistoryRecord {
  id: string;
  food_type: string;
  brand: string | null;
  product_name: string | null;
  feeding_frequency: string;
  portion_size: string | null;
  diet_start_date: string | null;
  diet_end_date: string | null;
  notes: string | null;
  created_at: string;
}

type FoodType = 'dry_kibble' | 'wet_food' | 'barf' | 'homemade' | 'prescription' | 'grass' | 'hay' | 'forage' | 'herbs' | 'silage' | 'concentrate';
type FeedingFrequency = 'once_daily' | 'twice_daily' | 'three_times' | 'free_feeding';

const FOOD_TYPE_LABELS: Record<string, string> = {
  dry_kibble: 'nutrition.foodTypes.dryKibble',
  wet_food: 'nutrition.foodTypes.wetFood',
  barf: 'nutrition.foodTypes.barf',
  homemade: 'nutrition.foodTypes.homemade',
  prescription: 'nutrition.foodTypes.prescription',
  grass: 'nutrition.foodTypes.grass',
  hay: 'nutrition.foodTypes.hay',
  forage: 'nutrition.foodTypes.forage',
  herbs: 'nutrition.foodTypes.herbs',
  silage: 'nutrition.foodTypes.silage',
  concentrate: 'nutrition.foodTypes.concentrate',
};

const FREQUENCY_LABELS: Record<string, string> = {
  once_daily: 'nutrition.frequency.onceDaily',
  twice_daily: 'nutrition.frequency.twiceDaily',
  three_times: 'nutrition.frequency.threeTimes',
  free_feeding: 'nutrition.frequency.freeFeeding',
};

const FOOD_TYPES: { value: FoodType; labelKey: string }[] = [
  { value: 'dry_kibble', labelKey: 'nutrition.foodTypes.dryKibble' },
  { value: 'wet_food', labelKey: 'nutrition.foodTypes.wetFood' },
  { value: 'barf', labelKey: 'nutrition.foodTypes.barf' },
  { value: 'homemade', labelKey: 'nutrition.foodTypes.homemade' },
  { value: 'prescription', labelKey: 'nutrition.foodTypes.prescription' },
  { value: 'grass', labelKey: 'nutrition.foodTypes.grass' },
  { value: 'hay', labelKey: 'nutrition.foodTypes.hay' },
  { value: 'forage', labelKey: 'nutrition.foodTypes.forage' },
  { value: 'herbs', labelKey: 'nutrition.foodTypes.herbs' },
  { value: 'silage', labelKey: 'nutrition.foodTypes.silage' },
  { value: 'concentrate', labelKey: 'nutrition.foodTypes.concentrate' },
];

const FEEDING_FREQUENCIES: { value: FeedingFrequency; labelKey: string }[] = [
  { value: 'once_daily', labelKey: 'nutrition.frequency.onceDaily' },
  { value: 'twice_daily', labelKey: 'nutrition.frequency.twiceDaily' },
  { value: 'three_times', labelKey: 'nutrition.frequency.threeTimes' },
  { value: 'free_feeding', labelKey: 'nutrition.frequency.freeFeeding' },
];

export const NutritionHistory = ({ petId, onHistoryChange }: NutritionHistoryProps) => {
  const { t } = useTranslation();
  const [history, setHistory] = useState<HistoryRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<HistoryRecord>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('pet_nutrition_history')
        .select('*')
        .eq('pet_id', petId)
        .order('diet_end_date', { ascending: false });

      if (error) throw error;
      setHistory(data || []);
    } catch (error) {
      console.error('Error loading nutrition history:', error);
    } finally {
      setIsLoading(false);
    }
  }, [petId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Validate dates don't overlap with other records
  const validateDates = (startDate: string | null, endDate: string | null, recordId: string): string | null => {
    if (!startDate || !endDate) return null;

    const start = parseISO(startDate);
    const end = parseISO(endDate);

    // Check start is before end
    if (isAfter(start, end)) {
      return t('nutrition.historyEdit.dateErrorStartAfterEnd');
    }

    // Check for overlaps with other records
    for (const record of history) {
      if (record.id === recordId) continue; // Skip the record being edited
      
      if (!record.diet_start_date || !record.diet_end_date) continue;

      const recordStart = parseISO(record.diet_start_date);
      const recordEnd = parseISO(record.diet_end_date);

      // Check if ranges overlap
      const overlaps = (
        (isAfter(start, recordStart) || isEqual(start, recordStart)) && 
        (isBefore(start, recordEnd) || isEqual(start, recordEnd))
      ) || (
        (isAfter(end, recordStart) || isEqual(end, recordStart)) && 
        (isBefore(end, recordEnd) || isEqual(end, recordEnd))
      ) || (
        (isBefore(start, recordStart) || isEqual(start, recordStart)) && 
        (isAfter(end, recordEnd) || isEqual(end, recordEnd))
      );

      if (overlaps) {
        return t('nutrition.historyEdit.dateErrorOverlap', {
          startDate: format(recordStart, 'MMM d, yyyy'),
          endDate: format(recordEnd, 'MMM d, yyyy')
        });
      }
    }

    return null;
  };

  const handleEdit = (record: HistoryRecord) => {
    setEditingId(record.id);
    setEditForm({
      food_type: record.food_type,
      brand: record.brand,
      product_name: record.product_name,
      feeding_frequency: record.feeding_frequency,
      portion_size: record.portion_size,
      diet_start_date: record.diet_start_date,
      diet_end_date: record.diet_end_date,
      notes: record.notes,
    });
    setDateError(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
    setDateError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;

    // Validate dates
    const error = validateDates(
      editForm.diet_start_date || null, 
      editForm.diet_end_date || null, 
      editingId
    );
    
    if (error) {
      setDateError(error);
      return;
    }

    setIsSaving(true);
    try {
      const { error: updateError } = await supabase
        .from('pet_nutrition_history')
        .update({
          food_type: editForm.food_type as FoodType,
          brand: editForm.brand || null,
          product_name: editForm.product_name || null,
          feeding_frequency: editForm.feeding_frequency as FeedingFrequency,
          portion_size: editForm.portion_size || null,
          diet_start_date: editForm.diet_start_date || null,
          diet_end_date: editForm.diet_end_date || null,
          notes: editForm.notes || null,
        })
        .eq('id', editingId);

      if (updateError) throw updateError;

      toast.success(t('nutrition.historyEdit.updateSuccess'));
      setEditingId(null);
      setEditForm({});
      setDateError(null);
      await loadHistory();
    } catch (error) {
      console.error('Error updating history record:', error);
      toast.error(t('nutrition.historyEdit.updateError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('pet_nutrition_history')
        .delete()
        .eq('id', deleteId);

      if (error) throw error;

      toast.success(t('nutrition.historyEdit.deleteSuccess'));
      setDeleteId(null);
      await loadHistory();
      
      // Notify parent to refresh its history cache for date validation
      onHistoryChange?.();
    } catch (error) {
      console.error('Error deleting history record:', error);
      toast.error(t('nutrition.historyEdit.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-12">
        <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">{t('nutrition.noHistory')}</p>
        <p className="text-sm text-muted-foreground mt-2">{t('nutrition.noHistoryHint')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4 py-4">
        {history.map((record) => (
          <div
            key={record.id}
            className="border rounded-lg p-4 bg-card"
          >
            {editingId === record.id ? (
              // Edit mode
              <div className="space-y-4">
                {dateError && (
                  <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>{dateError}</span>
                  </div>
                )}

                {/* Food Type */}
                <div className="space-y-2">
                  <Label>{t('nutrition.foodType')}</Label>
                  <Select
                    value={editForm.food_type}
                    onValueChange={(value) => setEditForm(prev => ({ ...prev, food_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FOOD_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {t(type.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Brand */}
                <div className="space-y-2">
                  <Label>{t('nutrition.brand')}</Label>
                  <BrandAutocomplete
                    value={editForm.brand || ''}
                    onChange={(value) => setEditForm(prev => ({ ...prev, brand: value }))}
                    isPrescription={editForm.food_type === 'prescription'}
                  />
                </div>

                {/* Product Name */}
                <div className="space-y-2">
                  <Label>{t('nutrition.productName')}</Label>
                  <Input
                    value={editForm.product_name || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, product_name: e.target.value }))}
                  />
                </div>

                {/* Feeding Frequency */}
                <div className="space-y-2">
                  <Label>{t('nutrition.feedingFrequency')}</Label>
                  <Select
                    value={editForm.feeding_frequency}
                    onValueChange={(value) => setEditForm(prev => ({ ...prev, feeding_frequency: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FEEDING_FREQUENCIES.map(freq => (
                        <SelectItem key={freq.value} value={freq.value}>
                          {t(freq.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Portion Size */}
                <div className="space-y-2">
                  <Label>{t('nutrition.portionSize')}</Label>
                  <Input
                    value={editForm.portion_size || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, portion_size: e.target.value }))}
                  />
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('nutrition.dietStartDate')}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !editForm.diet_start_date && "text-muted-foreground"
                          )}
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {editForm.diet_start_date ? (
                            format(parseISO(editForm.diet_start_date), "MMM d, yyyy")
                          ) : (
                            <span>{t('nutrition.selectDate')}</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={editForm.diet_start_date ? parseISO(editForm.diet_start_date) : undefined}
                          onSelect={(date) => {
                            setEditForm(prev => ({ 
                              ...prev, 
                              diet_start_date: date ? format(date, 'yyyy-MM-dd') : null 
                            }));
                            setDateError(null);
                          }}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('nutrition.historyEdit.dietEndDate')}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !editForm.diet_end_date && "text-muted-foreground"
                          )}
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {editForm.diet_end_date ? (
                            format(parseISO(editForm.diet_end_date), "MMM d, yyyy")
                          ) : (
                            <span>{t('nutrition.selectDate')}</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={editForm.diet_end_date ? parseISO(editForm.diet_end_date) : undefined}
                          onSelect={(date) => {
                            setEditForm(prev => ({ 
                              ...prev, 
                              diet_end_date: date ? format(date, 'yyyy-MM-dd') : null 
                            }));
                            setDateError(null);
                          }}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label>{t('nutrition.notes')}</Label>
                  <Textarea
                    value={editForm.notes || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                    rows={2}
                  />
                </div>

                {/* Edit Actions */}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={handleCancelEdit} disabled={isSaving}>
                    <X className="h-4 w-4 mr-1" />
                    {t('common.cancel')}
                  </Button>
                  <Button size="sm" onClick={handleSaveEdit} disabled={isSaving}>
                    {isSaving ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <Check className="h-4 w-4 mr-1" />
                    )}
                    {t('common.save')}
                  </Button>
                </div>
              </div>
            ) : (
              // View mode
              <>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <Badge variant="secondary">
                      {t(FOOD_TYPE_LABELS[record.food_type] || record.food_type)}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      {record.diet_start_date && (
                        <span>
                          {format(new Date(record.diet_start_date), 'MMM d, yyyy')}
                        </span>
                      )}
                      {record.diet_end_date && (
                        <>
                          <span>→</span>
                          <span>{format(new Date(record.diet_end_date), 'MMM d, yyyy')}</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleEdit(record)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => setDeleteId(record.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="space-y-1">
                  {record.brand && (
                    <p className="font-medium">{record.brand}</p>
                  )}
                  {record.product_name && (
                    <p className="text-sm text-muted-foreground">{record.product_name}</p>
                  )}
                  <p className="text-sm">
                    <span className="text-muted-foreground">{t('nutrition.feedingFrequency')}: </span>
                    {t(FREQUENCY_LABELS[record.feeding_frequency] || record.feeding_frequency)}
                  </p>
                  {record.portion_size && (
                    <p className="text-sm">
                      <span className="text-muted-foreground">{t('nutrition.portionSize')}: </span>
                      {record.portion_size}
                    </p>
                  )}
                  {record.notes && (
                    <p className="text-sm text-muted-foreground mt-2 italic">"{record.notes}"</p>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('nutrition.historyEdit.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('nutrition.historyEdit.deleteConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
              {isDeleting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
